"use client";

import { useDeferredValue, useEffect, useMemo, useState } from "react";

export const useDebouncedValue = <T,>(value: T, delay = 300) => {
	const [isPending, setIsPending] = useState(false);
	const [debouncedValue, setDebouncedValue] = useState(value);

	useEffect(() => {
		setIsPending(true);
		const handler = setTimeout(() => {
			setIsPending(false);
			setDebouncedValue(value);
		}, delay);

		return () => {
			setIsPending(false);
			clearTimeout(handler);
		};
	}, [value, delay]);

	return [isPending, useDeferredValue(debouncedValue)] as const;
};

export const debounce = <TArgs extends unknown[]>(
	fn: (...args: TArgs) => void,
	delay: number,
	{ leading = false, trailing = true }: { trailing?: boolean; leading?: boolean } = {},
) => {
	if (leading === false && trailing === false) {
		throw new Error("At least one of 'leading' or 'trailing' must be true");
	}

	let timeout: NodeJS.Timeout | null;

	return (...args: TArgs): void => {
		if (leading && !timeout) {
			fn.apply(null, args);
		}

		if (timeout) {
			clearTimeout(timeout);
		}

		timeout = setTimeout(() => {
			timeout = null;

			if (trailing) {
				fn.apply(null, args);
			}
		}, delay);
	};
};

export const useDebounce = <TArgs extends unknown[], TReturn>(
	fn: (...args: TArgs) => TReturn,
	delay: number,
) => {
	// biome-ignore lint/correctness/useExhaustiveDependencies: <missing explanation>
	return useMemo(() => debounce(fn, delay), [delay]);
};

// @todo this function is more of a throttle than a debounce
export const asyncDebounce = <TArgs extends unknown[], TReturn>(
	fn: (...args: TArgs) => Promise<TReturn>,
	delay: number,
): ((...args: TArgs) => Promise<TReturn>) => {
	let timeout: NodeJS.Timeout;
	let latestArgs: TArgs;
	let pendingPromise: Promise<TReturn> | null = null;

	return (...args: TArgs): Promise<TReturn> => {
		latestArgs = args;

		// If there's already a pending promise, return it
		if (pendingPromise) {
			return pendingPromise;
		}

		// Create a new promise that will resolve with the latest result
		pendingPromise = new Promise<TReturn>((resolve, reject) => {
			const executeFunction = async () => {
				try {
					const result = await fn(...latestArgs);
					pendingPromise = null;
					resolve(result);
				} catch (error) {
					pendingPromise = null;
					reject(error);
				}
			};

			// Clear existing timeout
			if (timeout) {
				clearTimeout(timeout);
			}

			// Set new timeout
			timeout = setTimeout(executeFunction, delay);
		});

		return pendingPromise;
	};
};
